import * as gl from "GlobalConfig";

let initialized = false;
let userTracked = false;

const CUSTOM_DIMENSIONS = {
  userName: 1,
  role: 2,
  facility: 3,
  enterprise: 4,
  userGroup: 5,
};

function hostOf(url) {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch (e) {
    return String(url || "").trim().toLowerCase();
  }
}

// Enabled only when Matomo URL + site id are set, and (if an app domain is
// configured) the current site's hostname matches it. Empty URL = disabled.
export function isMatomoEnabled() {
  const url = (gl.MatomoUrl || "").trim();
  const siteId = (gl.MatomoSiteId || "").trim();
  if (!url || !siteId) return false;

  const appUrl = (gl.MatomoAppUrl || "").trim();
  if (appUrl && typeof window !== "undefined") {
    return window.location.hostname.toLowerCase() === hostOf(appUrl);
  }
  return true;
}

function paq() {
  if (typeof window === "undefined") return [];
  window._paq = window._paq || [];
  return window._paq;
}

function normaliseBaseUrl(url) {
  if (!url) return "";
  return url.endsWith("/") ? url : `${url}/`;
}

export function initMatomo() {
  if (initialized || !isMatomoEnabled() || typeof document === "undefined") {
    return;
  }

  const baseUrl = normaliseBaseUrl(gl.MatomoUrl);
  const _paq = paq();

  _paq.push(["setTrackerUrl", `${baseUrl}matomo.php`]);
  _paq.push(["setSiteId", String(gl.MatomoSiteId)]);
  _paq.push(["setDoNotTrack", true]);
  _paq.push(["enableHeartBeatTimer", 15]);
  _paq.push(["enableLinkTracking"]);
  _paq.push(["trackVisibleContentImpressions"]);
  _paq.push(["setDownloadClasses", "matomo_download"]);
  _paq.push(["setLinkClasses", "matomo_link"]);
  _paq.push(["trackPageView"]);

  const scriptSrc = `${baseUrl}matomo.js`;
  const existing = document.querySelector(`script[src="${scriptSrc}"]`);
  if (!existing) {
    const g = document.createElement("script");
    const s = document.getElementsByTagName("script")[0];
    g.type = "text/javascript";
    g.async = true;
    g.defer = true;
    g.src = scriptSrc;
    if (s && s.parentNode) {
      s.parentNode.insertBefore(g, s);
    } else {
      document.head.appendChild(g);
    }
  }

  enableErrorTracking();

  initialized = true;
}

export function trackPageView(path, title) {
  if (!isMatomoEnabled()) return;
  const _paq = paq();
  const url =
    path ||
    (typeof window !== "undefined"
      ? window.location.pathname + window.location.search
      : "");

  if (typeof window !== "undefined") {
    _paq.push(["setReferrerUrl", window.location.href]);
    _paq.push(["setCustomUrl", window.location.origin + url]);
  }
  if (title || (typeof document !== "undefined" && document.title)) {
    _paq.push(["setDocumentTitle", title || document.title]);
  }
  _paq.push(["trackPageView"]);
  _paq.push(["enableLinkTracking"]);
  _paq.push(["trackVisibleContentImpressions"]);
}

export function buildUserDetails(user) {
  if (!user) return null;
  const employee = user.employeeData || {};
  const userId =
    user.userId != null ? String(user.userId) : String(employee.userId || "");
  const employeeName =
    employee.employeeName ||
    [employee.titleName, employee.firstName, employee.middleName, employee.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    "";

  return {
    userId,
    userName: employeeName,
    role: user.roleName || "",
    roleId: user.roleId != null ? String(user.roleId) : "",
    facility: user.facilityName || "",
    facilityId: user.facilityId != null ? String(user.facilityId) : "",
    enterprise: user.enterpriseName || "",
    enterpriseId: user.enterpriseId != null ? String(user.enterpriseId) : "",
    userGroup: employee.userGroupName || employee.userGroup || "",
    email: employee.employee_email || "",
  };
}

export function setMatomoUser(user) {
  if (!isMatomoEnabled()) return;
  const details = buildUserDetails(user);
  if (!details) return;

  const _paq = paq();

  if (details.userId) {
    _paq.push(["setUserId", details.userId]);
  }

  const dims = [
    [CUSTOM_DIMENSIONS.userName, details.userName],
    [CUSTOM_DIMENSIONS.role, details.role],
    [CUSTOM_DIMENSIONS.facility, details.facility],
    [CUSTOM_DIMENSIONS.enterprise, details.enterprise],
    [CUSTOM_DIMENSIONS.userGroup, details.userGroup],
  ];
  dims.forEach(([id, value]) => {
    if (id && value) {
      _paq.push(["setCustomDimension", id, String(value)]);
    }
  });

  const customVars = [
    [1, "UserName", details.userName],
    [2, "Role", details.role],
    [3, "Facility", details.facility],
    [4, "Enterprise", details.enterprise],
    [5, "UserGroup", details.userGroup],
  ];
  customVars.forEach(([index, name, value]) => {
    if (value) {
      _paq.push(["setCustomVariable", index, name, String(value), "visit"]);
    }
  });

  if (!userTracked) {
    _paq.push([
      "trackEvent",
      "User",
      "Login",
      details.userName || details.userId,
    ]);
    userTracked = true;
  }
}

export function resetMatomoUser() {
  if (!isMatomoEnabled()) return;
  const _paq = paq();
  _paq.push(["trackEvent", "User", "Logout"]);
  _paq.push(["resetUserId"]);
  _paq.push(["deleteCustomVariables", "visit"]);
  Object.values(CUSTOM_DIMENSIONS).forEach((id) => {
    _paq.push(["deleteCustomDimension", id]);
  });
  _paq.push(["trackPageView"]);
  userTracked = false;
}

export function trackEvent(category, action, name, value) {
  if (!isMatomoEnabled() || !category || !action) return;
  const _paq = paq();
  const event = ["trackEvent", category, action];
  if (name != null) event.push(String(name));
  if (value != null) event.push(Number(value));
  _paq.push(event);
}

export function trackSearch(keyword, category, resultCount) {
  if (!isMatomoEnabled() || !keyword) return;
  const _paq = paq();
  _paq.push([
    "trackSiteSearch",
    String(keyword),
    category ? String(category) : false,
    resultCount != null ? Number(resultCount) : false,
  ]);
}

export function trackContentInteraction(contentName, contentPiece, contentTarget) {
  if (!isMatomoEnabled() || !contentName) return;
  const _paq = paq();
  _paq.push([
    "trackContentInteraction",
    "click",
    String(contentName),
    contentPiece ? String(contentPiece) : "Unknown",
    contentTarget ? String(contentTarget) : "",
  ]);
}

export function trackDownload(url) {
  if (!isMatomoEnabled() || !url) return;
  paq().push(["trackLink", String(url), "download"]);
}

export function trackOutlink(url) {
  if (!isMatomoEnabled() || !url) return;
  paq().push(["trackLink", String(url), "link"]);
}

export function trackGoal(goalId, value) {
  if (!isMatomoEnabled() || goalId == null) return;
  const _paq = paq();
  if (value != null) _paq.push(["trackGoal", Number(goalId), Number(value)]);
  else _paq.push(["trackGoal", Number(goalId)]);
}

let errorTrackingEnabled = false;

export function enableErrorTracking() {
  if (errorTrackingEnabled || typeof window === "undefined") return;
  errorTrackingEnabled = true;

  window.addEventListener("error", (e) => {
    try {
      const where = `${e.filename || "unknown"}:${e.lineno || 0}:${e.colno || 0}`;
      trackEvent("JavaScript Error", e.message || "Error", where);
    } catch (_) {
      /* empty */
    }
  });

  window.addEventListener("unhandledrejection", (e) => {
    try {
      const reason =
        (e.reason && (e.reason.message || String(e.reason))) ||
        "Unhandled promise rejection";
      trackEvent("JavaScript Error", "UnhandledRejection", reason);
    } catch (_) {
      /* empty */
    }
  });
}

export default {
  isMatomoEnabled,
  initMatomo,
  trackPageView,
  buildUserDetails,
  setMatomoUser,
  resetMatomoUser,
  trackEvent,
  trackSearch,
  trackContentInteraction,
  trackDownload,
  trackOutlink,
  trackGoal,
  enableErrorTracking,
};
